import React from "react";
import { Folder, Loader2 } from "lucide-react";

import { personName } from "@/shared/api/members";
import { FileTypeIcon } from "@/shared/components/icons/filetype";
import InitialsAvatar from "@/shared/components/InitialsAvatar";
import { ago } from "@/shared/lib/time";
import { KbNodeMenu } from "./KbNodeMenu";
import { FileStatusBadge } from "./KbStatus";
import { extOf, formatSize, plural } from "./format";
import { useFileDrop } from "./hooks/useFileDrop";
import { useInlineRename } from "./hooks/useInlineRename";
import { STATUS_LABEL, statusOf, type Row } from "./listing";
import type { KbTree } from "./hooks/useTree";
import type { PendingUpload } from "./hooks/useKbUpload";

import type { KbNode, KbUpload, PersonRef } from "@/shared/types";

/* The table's columns: Name, Owner, Status, Modified, Tokens, Size, ⋮. The ones after Name
   drop out whole as the room shrinks (DESIGN §12.7) — the rail and the app sidebar take
   their share — and Name, Size and ⋮ always stay. */
export const GRID =
  "grid items-center gap-3 grid-cols-[minmax(0,1fr)_72px_28px] sm:grid-cols-[minmax(0,1fr)_108px_72px_28px] " +
  "lg:grid-cols-[minmax(0,1fr)_108px_80px_72px_28px] xl:grid-cols-[minmax(0,1fr)_144px_108px_80px_72px_28px] " +
  "min-[1440px]:grid-cols-[minmax(0,1fr)_144px_108px_104px_80px_72px_28px]";
export const SHOW = {
  owner: "hidden xl:flex",
  status: "hidden sm:flex",
  modified: "hidden min-[1440px]:flex",
  tokens: "hidden lg:flex justify-end",
};

export interface NodeEdits {
  rename: (node: KbNode, name: string) => void;
  move: (node: KbNode) => void;
  remove: (node: KbNode) => void;
  reindex: (node: KbNode) => void;
  download: (node: KbNode) => void;
}

const numberCls = "text-[12px] font-mono tabular-nums text-[var(--text-tertiary)]";
const NONE = <span className="text-[var(--text-quaternary)]">—</span>;

/* `ts_headline` wraps the match in `<mark>`. Split on the markers and render the
   pieces as text, so nothing in a snippet can become markup. */
export function Snippet({ text }: { text: string }) {
  const parts = text.split(/(<mark>|<\/mark>)/);
  let marked = false;
  return (
    <>
      {parts.map((part, i) => {
        if (part === "<mark>") { marked = true; return null; }
        if (part === "</mark>") { marked = false; return null; }
        return marked
          ? <mark key={i} className="rounded-[3px] px-[2px] bg-[var(--info-bg)] font-medium text-[var(--text-primary)]">{part}</mark>
          : <span key={i}>{part}</span>;
      })}
    </>
  );
}

/** On a phone the Status column steps aside; a file that is not readable yet keeps a dot. */
function PhoneStatus({ node }: { node: KbNode }) {
  const status = statusOf(node);
  if (status !== "failed" && status !== "processing") return null;
  return (
    <span title={STATUS_LABEL[status]} className={`sm:hidden w-1.5 h-1.5 shrink-0 rounded-full ${status === "failed" ? "bg-[var(--danger)]" : "bg-[var(--info)] motion-safe:animate-pulse"}`}>
      <span className="sr-only">{STATUS_LABEL[status]}</span>
    </span>
  );
}

/** "You" for the reader, the person's name otherwise, "—" when nobody is on record. */
function Owner({ person, me }: { person: PersonRef | null; me?: string }) {
  if (!person) return NONE;
  const name = personName(person);
  return (
    <span className="min-w-0 flex items-center gap-2" title={person.email}>
      <InitialsAvatar name={name} size={20} />
      <span className="truncate text-[12.5px] text-[var(--text-secondary)]">{person.id === me ? "You" : name}</span>
    </span>
  );
}

export function KbRow({ row, tree, me, onOpen, uploading = false, edits, onDrop }: {
  row: Row;
  tree: KbTree;
  /** The reader's user id, to say "You". */
  me?: string;
  onOpen: () => void;
  /** A folder files are on their way into. */
  uploading?: boolean;
  edits: NodeEdits;
  /** A folder row takes dropped files and folders into that folder. */
  onDrop?: (uploads: KbUpload[]) => void;
}) {
  const { node } = row;
  const isFolder = node.kind === "folder";
  const rename = useInlineRename(node.name, (name) => edits.rename(node, name));
  const drop = useFileDrop(isFolder ? onDrop : undefined);
  const sums = node.file ? { files: 1, bytes: node.file.bytes, tokens: node.file.tokens } : tree.totals(node.id);
  const stop = (e: React.SyntheticEvent) => e.stopPropagation(); // the menu's portal bubbles through here
  const modified = Date.parse(node.updated_at) || 0; // an unreadable date shows "—" rather than throwing

  return (
    <div
      role="row"
      data-node-row
      title={node.name}
      onClick={rename.editing ? undefined : onOpen}
      {...drop.props}
      className={`${GRID} group px-3 min-h-[44px] py-1.5 cursor-pointer ${drop.over ? "bg-[var(--info-bg)] ring-1 ring-inset ring-[var(--info)]" : "hover:bg-[var(--accent-hover)]"}`}
    >
      <div role="cell" className="min-w-0 flex items-center gap-2.5">
        {uploading ? (
          <Loader2 aria-label="Uploading" className="w-[18px] h-[18px] mx-px shrink-0 text-[var(--text-tertiary)] animate-spin" strokeWidth={1.75} />
        ) : isFolder ? (
          <Folder className="w-[18px] h-[18px] mx-px shrink-0 text-[var(--text-secondary)]" strokeWidth={1.75} fill="currentColor" fillOpacity={0.12} />
        ) : (
          <FileTypeIcon kind={extOf(node.name) || "file"} size={20} />
        )}
        <div className="min-w-0 flex-1">
          {rename.editing ? (
            <input
              {...rename.inputProps}
              onClick={stop}
              className="w-full min-w-0 h-7 px-2 -ml-2 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-focus)] outline-none text-[13.5px] text-[var(--text-primary)]"
            />
          ) : (
            <span className="min-w-0 flex items-center gap-1.5">
              <button
                type="button"
                className={`min-w-0 block truncate text-left text-[13.5px] tracking-[-0.005em] text-[var(--text-primary)] outline-none rounded-[4px] focus-visible:ring-2 focus-visible:ring-[var(--hairline-focus)] ${isFolder ? "font-medium" : ""}`}
              >
                {node.name}
              </button>
              <PhoneStatus node={node} />
            </span>
          )}
          {row.where && <div className="truncate text-[12px] text-[var(--text-tertiary)]">{row.where}</div>}
          {row.snippet && (
            <div className="truncate text-[12px] text-[var(--text-tertiary)]">
              <Snippet text={row.snippet} />
            </div>
          )}
        </div>
      </div>
      <div role="cell" className={`${SHOW.owner} min-w-0 items-center`}>
        <Owner person={node.created_by} me={me} />
      </div>
      <div role="cell" className={`${SHOW.status} items-center`}>
        <FileStatusBadge node={node} />
      </div>
      <div role="cell" className={`${SHOW.modified} items-center text-[12.5px] tabular-nums text-[var(--text-tertiary)] whitespace-nowrap`} title={modified ? new Date(modified).toLocaleString() : undefined}>
        {modified ? ago(node.updated_at) : NONE}
      </div>
      <div role="cell" className={`${SHOW.tokens} ${numberCls}`}>
        {sums.tokens ? sums.tokens.toLocaleString("en-US") : NONE}
      </div>
      <div role="cell" className={`flex justify-end whitespace-nowrap ${numberCls}`} title={isFolder ? plural(sums.files, "file") : undefined}>
        {sums.files ? formatSize(sums.bytes) : NONE}
      </div>
      <div role="cell" onClick={stop} className="flex justify-end">
        <KbNodeMenu
          node={node}
          className="lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100 lg:data-[state=open]:opacity-100"
          actions={{
            onOpen,
            onDownload: () => edits.download(node),
            onRename: rename.begin,
            onMove: () => edits.move(node),
            onReindex: () => edits.reindex(node),
            onDelete: () => edits.remove(node),
          }}
        />
      </div>
    </div>
  );
}

/** A file or folder in the request in flight; the refetched tree replaces it. */
export function UploadingRow({ pending }: { pending: PendingUpload }) {
  return (
    <div role="row" className={`${GRID} px-3 min-h-[44px] py-1.5 fade-in`}>
      <span role="cell" className="min-w-0 flex items-center gap-2.5">
        <Loader2 className="w-[18px] h-[18px] mx-px shrink-0 text-[var(--text-tertiary)] animate-spin" strokeWidth={1.75} />
        <span className="flex-1 min-w-0 truncate text-[13.5px] text-[var(--text-primary)] tracking-[-0.005em]">{pending.name}</span>
      </span>
      <span role="cell" className={SHOW.owner} />
      <span role="cell" className={`${SHOW.status} text-[12px] text-[var(--text-tertiary)] whitespace-nowrap`}>Uploading…</span>
      <span role="cell" className={SHOW.modified} />
      <span role="cell" className={SHOW.tokens} />
      <span role="cell" className={`flex justify-end whitespace-nowrap ${numberCls}`}>
        <span className="sm:hidden font-sans">Uploading…</span>
        <span className="hidden sm:inline">{pending.files > 1 ? plural(pending.files, "file") : ""}</span>
      </span>
      <span role="cell" />
    </div>
  );
}
